<template>
  <SFSlideIn
    id="test-size"
    :name="SIZE_BACK_IN_STOCK_SLIDEIN"
    borderless
    mobile-height="h-2/3"
    slide-class="w-full md:h-full md:min-w-[448px] border xl:inset-y-0 !p-0 !bg-gray-100"
    slide-header-class="!bg-gray-100"
  >
    <!-- Header -->
    <template #slide-in-header>
      <div class="flex flex-col gap-2 px-4 font-sans">
        <div class="flex items-center justify-between">
          <div class="size-1.5"></div>
          <h3 class="text-xl font-bold leading-[150%] text-black">
            {{ modalTile }}
          </h3>
          <SFButton variant="raw" @click="toggleBackInStockSidebar">
            <IconSnipesFeedbackx class="size-6 text-anthracite-500" />
          </SFButton>
        </div>
      </div>
    </template>
    <!-- Body -->
    <template #slide-in-body>
      <div class="space-y-4 px-4 py-3">
        <p class="text-center text-xl">{{ modalDescription }}</p>
        <SFModalItemCard
          v-if="isDataLoaded"
          :product="product"
          :tab="tab"
          :variant="variant"
          class="mb-4"
          has-border
          has-color-box
        />
        <div v-else>
          <SFSkeletonLoader class="h-52 w-full" type="custom" />
        </div>
        <form
          v-if="!isNotifySubscribed"
          class="space-y-4"
          @submit.prevent="onSubmit"
        >
          <SFValidatedInputGroup
            v-slot="{ isValid }"
            :errors="v.email.$errors"
            class="relative"
          >
            <SFTextInput
              id="login-email"
              v-model="email"
              :has-errors="!isValid"
              :placeholder="$t('form_fields.email')"
              autocomplete="email"
              bg-color="bg-gray-100"
              class="text-base"
              name="login-email"
              @change="v.email.$touch()"
            />
            <IconMail class="absolute right-2 top-4 size-6" />
          </SFValidatedInputGroup>
          <p v-if="!isNotifySubscribed">
            <I18nT keypath="product_back_in_stock.privacy" tag="p">
              <template #privacy>
                <SFLink
                  class="underline"
                  raw
                  :to="getLocalizedRoute('/s/privacypolicy')"
                >
                  {{ $t('global.privacy') }}
                </SFLink>
              </template>
            </I18nT>
          </p>
          <div v-if="!isNotifySubscribed" class="flex space-x-4">
            <SFButton
              :disabled="isSubmitting"
              is-full-width
              type="button"
              variant="tertiary"
              @click="toggleBackInStockSidebar"
            >
              {{ t('global.cancel') }}</SFButton
            >
            <SFButton :disabled="isSubmitting" is-full-width type="submit">
              {{ t('global.save') }}</SFButton
            >
          </div>
        </form>
        <div
          v-else
          class="absolute inset-x-4 bottom-5 pt-4 md:relative md:inset-x-0"
        >
          <SFButton is-full-width @click="toggleBackInStockSidebar">
            {{ $t('global.okay') }}
          </SFButton>
        </div>
      </div>
    </template>
  </SFSlideIn>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import useVuelidate from '@vuelidate/core'
import type { Variant } from '@scayle/storefront-nuxt'
import { getFirstAttributeValue } from '@scayle/storefront-nuxt'
import {
  SFButton,
  SFSkeletonLoader,
  SFSlideIn,
  SFTextInput,
  SFValidatedInputGroup,
} from '~/modules/ui/runtime/components'
import {
  SHOE_SIZES_ATTRIBUTES_KEYS,
  SIZE_BACK_IN_STOCK_SLIDEIN,
} from '~/constants'
import { I18nT, useI18n } from '#i18n'
import { SFLink } from '#storefront-ui/components'
import {
  useFormat,
  useProductBaseInfo,
  useRouteHelpers,
  useSnipesApi,
  useTrackingEvents,
  useValidationRules,
} from '~/composables'
import SFModalItemCard from '~/components/SFModalItemCard.vue'
import useBackInStockModal from '~/composables/useBackInStockModal'

const { t } = useI18n()
const { formatShoeSize } = useFormat()
const validationRules = useValidationRules()
const { getLocalizedRoute } = useRouteHelpers()
const { trackBasketInStock } = useTrackingEvents()
const { notifyBackInStock, isLoading: isSubmitting } = useSnipesApi()
const { toggleBackInStockSidebar, product, variant, tab } =
  useBackInStockModal()
const { isShoeProduct } = useProductBaseInfo(() => product.value)
const email = ref<string>('')
const isNotifySubscribed = ref(false)

const modalTile = computed(() =>
  !isNotifySubscribed.value
    ? t('product_back_in_stock.title')
    : t('product_back_in_stock.subscribed.title'),
)
const modalDescription = computed(() =>
  !isNotifySubscribed.value
    ? t('product_back_in_stock.message')
    : t('product_back_in_stock.subscribed.message'),
)

const rules = {
  email: {
    required: validationRules.required,
    email: validationRules.email,
  },
}

const v = useVuelidate(rules, { email })
const isDataLoaded = computed(() => product && variant && tab)

const getVariantSizeLabel = (variant: Variant, tab: string) => {
  if (isShoeProduct.value) {
    return formatShoeSize(
      getFirstAttributeValue(
        variant.attributes,
        SHOE_SIZES_ATTRIBUTES_KEYS[
          tab as keyof typeof SHOE_SIZES_ATTRIBUTES_KEYS
        ],
      )?.label || '',
    )
  }
  return getFirstAttributeValue(variant.attributes, 'sizeOther')?.label || ''
}

const onSubmit = async () => {
  const isValid = await v.value.$validate()

  if (!isValid || isSubmitting.value) {
    return
  }
  const payload = {
    email: email.value,
    product_sku: variant.value.referenceKey,
    source: 'PDP', // for now
    additional_fields: [],
  }
  try {
    const size = getVariantSizeLabel(variant.value, tab.value)
    trackBasketInStock(size, product.value?.id)
    await notifyBackInStock(payload)
    isNotifySubscribed.value = true
  } catch {
    //  empty block
  }
}
</script>
